import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { toast } from 'react-hot-toast';
import { createOrder } from '../utils/api';
import { roundRupees } from '../utils/money';
import { useOrderFooterInset } from '../hooks/useOrderFooterInset';
import {
  getWholesaleQtyError,
  mapWholesaleLinesToOrderItems,
  calcWholesaleTotals,
  distributeAdvanceAcrossLines,
  validateWholesaleCustomer,
  WHOLESALE_CATEGORY_ORDER,
} from '../utils/wholesaleOrderForm';
import ModelPicker from './ModelPicker';
import OrderFormStickyBar from './OrderFormStickyBar';
import WholesaleModelSidePanel from './WholesaleModelSidePanel';
import WholesaleOrderBucketSheet from './WholesaleOrderBucketSheet';

const WholesaleOrderPanelForm = ({
  inventory = [],
  customerName,
  phone,
  customerAddress,
  onResetCustomer,
}) => {
  const [loading, setLoading] = useState(false);
  const [lastCreatedOrderReceipt, setLastCreatedOrderReceipt] = useState(null);
  const [panelModel, setPanelModel] = useState(null);
  const [bucketSheetOpen, setBucketSheetOpen] = useState(false);
  const [selectedLines, setSelectedLines] = useState({});
  const [settledPrice, setSettledPrice] = useState('');
  const [orderAdvance, setOrderAdvance] = useState(null);
  const formRef = useRef(null);
  const footerRef = useRef(null);

  useOrderFooterInset(formRef, footerRef);

  const linesArray = useMemo(() => Object.values(selectedLines), [selectedLines]);
  const selectedModelIds = useMemo(() => Object.keys(selectedLines), [selectedLines]);
  const selectedQtyById = useMemo(
    () => Object.fromEntries(linesArray.map((line) => [line.model, line.qty])),
    [linesArray]
  );

  const hasSettled =
    settledPrice !== '' &&
    settledPrice !== null &&
    settledPrice !== undefined &&
    Number.isFinite(Number(settledPrice));

  const {
    catalogGrandTotal,
    grandTotal,
    totalAdvance,
    balance,
    minAdvanceRequired,
    isAdvanceSufficient,
  } = calcWholesaleTotals(linesArray, {
    settledPrice: hasSettled ? settledPrice : undefined,
    orderAdvance: hasSettled ? orderAdvance : undefined,
  });

  const hasQtyErrors = linesArray.some((item) => getWholesaleQtyError(item));

  const groupedLines = useMemo(() => {
    const groups = {};
    for (const line of linesArray) {
      const cat = line.category || 'other';
      if (!groups[cat]) groups[cat] = [];
      groups[cat].push(line);
    }
    return WHOLESALE_CATEGORY_ORDER.filter((cat) => groups[cat]?.length)
      .map((cat) => ({ category: cat, lines: groups[cat] }))
      .concat(
        groups.other?.length
          ? [{ category: 'other', label: 'Other', lines: groups.other }]
          : []
      );
  }, [linesArray]);

  const handleModelClick = (inv) => {
    setBucketSheetOpen(false);
    setPanelModel(inv);
  };

  const handleAddToBucket = (line) => {
    const isUpdate = Boolean(selectedLines[line.model]);
    setSelectedLines((prev) => ({
      ...prev,
      [line.model]: line,
    }));
    setPanelModel(null);
    toast.success(isUpdate ? 'Bucket item updated' : 'Added to order bucket');
  };

  const handleRemoveFromBucket = (modelId) => {
    setSelectedLines((prev) => {
      const next = { ...prev };
      delete next[modelId];
      return next;
    });
  };

  const handleEditLine = (modelId) => {
    const inv = inventory.find((item) => item._id === modelId);
    if (inv) {
      setBucketSheetOpen(false);
      setPanelModel(inv);
    }
  };

  const resetBucket = useCallback(() => {
    setSelectedLines({});
    setPanelModel(null);
    setBucketSheetOpen(false);
    setSettledPrice('');
    setOrderAdvance(null);
  }, []);

  const resetForm = useCallback(() => {
    onResetCustomer?.();
    resetBucket();
  }, [onResetCustomer, resetBucket]);

  const handleClearForm = () => {
    resetForm();
    toast.success('Form cleared');
  };

  const handleSettledChange = (value) => {
    setSettledPrice(value);
    if (value === '') {
      setOrderAdvance(null);
      return;
    }
    const settled = roundRupees(Number(value) || 0);
    const lineAdvanceSum = roundRupees(linesArray.reduce((sum, item) => sum + item.advance, 0));
    const nextAdvance = Math.min(
      orderAdvance !== null && orderAdvance !== undefined ? roundRupees(orderAdvance) : lineAdvanceSum,
      settled
    );
    const minRequired = roundRupees(Math.ceil(settled * 0.4));
    setOrderAdvance(Math.max(nextAdvance, Math.min(minRequired, settled)));
  };

  const handleSettledClear = () => {
    setSettledPrice('');
    setOrderAdvance(null);
  };

  const handleAdvanceChange = (value) => {
    const next = Math.min(Math.max(0, roundRupees(value)), grandTotal);
    setOrderAdvance(next);
  };

  const handleAdvanceFull = () => {
    setOrderAdvance(grandTotal);
  };

  const handleAdvanceClear = () => {
    setOrderAdvance(0);
  };

  const validateForm = useCallback(() => {
    const customerError = validateWholesaleCustomer(customerName, phone);
    if (customerError) {
      toast.error(customerError);
      return false;
    }
    if (linesArray.length === 0) {
      toast.error('Please add at least one model to the order bucket');
      return false;
    }
    for (let i = 0; i < linesArray.length; i++) {
      const qtyError = getWholesaleQtyError(linesArray[i]);
      if (qtyError) {
        toast.error(`${linesArray[i].modelName}: ${qtyError}`);
        return false;
      }
    }
    if (hasSettled && Number(settledPrice) < 0) {
      toast.error('Agreed price cannot be negative');
      return false;
    }
    if (totalAdvance > grandTotal) {
      toast.error('Advance amount cannot exceed total order value');
      return false;
    }
    if (totalAdvance < minAdvanceRequired) {
      toast.error(
        `Minimum 40% advance required: ₹${minAdvanceRequired.toLocaleString('en-IN')}`
      );
      return false;
    }
    return true;
  }, [
    customerName,
    phone,
    linesArray,
    hasSettled,
    settledPrice,
    totalAdvance,
    grandTotal,
    minAdvanceRequired,
  ]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setLoading(true);
    try {
      let items = mapWholesaleLinesToOrderItems(linesArray);
      if (hasSettled) {
        const distributed = distributeAdvanceAcrossLines(linesArray, totalAdvance);
        items = mapWholesaleLinesToOrderItems(distributed);
      }

      const payload = {
        type: 'wholesale',
        customerName: customerName.trim(),
        phone: phone.trim(),
        customerAddress: customerAddress.trim(),
        items,
      };
      if (hasSettled) {
        payload.settledPrice = roundRupees(Number(settledPrice));
      }

      const result = await createOrder(payload);

      toast.success(`Wholesale order ${result.order.orderNo} created`);

      setLastCreatedOrderReceipt({
        orderId: result.order._id,
        orderNo: result.order.orderNo,
      });

      resetForm();
    } catch (error) {
      toast.error(error.message || 'Failed to create order');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const onKeyDown = (event) => {
      if (panelModel || bucketSheetOpen) return;
      if ((event.ctrlKey || event.metaKey) && event.key === 'Enter') {
        if (!loading && !hasQtyErrors && validateForm()) {
          document.getElementById('wholesale-order-panel-form')?.requestSubmit();
        }
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [loading, hasQtyErrors, validateForm, panelModel, bucketSheetOpen]);

  // When lines change under a settled price, keep order advance within the billed total.
  useEffect(() => {
    if (!hasSettled || orderAdvance === null || orderAdvance === undefined) return;
    if (orderAdvance > grandTotal) {
      setOrderAdvance(grandTotal);
    }
  }, [hasSettled, orderAdvance, grandTotal]);

  const formatMoney = (amount) => `₹${Number(amount || 0).toLocaleString('en-IN')}`;

  return (
    <div className="flex flex-col flex-1 min-h-0">
      <form
        ref={formRef}
        id="wholesale-order-panel-form"
        onSubmit={handleSubmit}
        className="order-form--mobile-overlay-footer bg-white rounded-lg shadow-xl flex-1 min-h-0 p-2 sm:p-6 md:grid"
      >
        <section className="flex flex-col min-h-0 overflow-hidden gap-2">
          <div className="hidden md:flex items-center justify-between gap-2 shrink-0">
            <h2 className="text-sm font-semibold text-gray-800 uppercase tracking-wide">Select models</h2>
            <span className="text-xs text-gray-500 text-right">
              Tap to add
              {linesArray.length > 0 ? ` · ${linesArray.length} in bucket` : ''}
            </span>
          </div>
          <ModelPicker
            inventory={inventory}
            multiSelect
            fillHeight
            compactMobile
            dynamicScrollInset
            selectedModelIds={selectedModelIds}
            selectedQtyById={selectedQtyById}
            onToggleSelect={handleModelClick}
            accentRing="ring-blue-500"
          />
        </section>

        <OrderFormStickyBar
          ref={footerRef}
          variant="inCard"
          grandTotal={grandTotal}
          catalogGrandTotal={catalogGrandTotal}
          totalAdvance={totalAdvance}
          balance={balance}
          loading={loading}
          submitDisabled={hasQtyErrors || linesArray.length === 0}
          submitLabel="Create Wholesale Order"
          submitClassName="admin-btn admin-btn-submit h-9 px-4 text-sm font-semibold rounded-md"
          lastCreatedOrderReceipt={lastCreatedOrderReceipt}
          minAdvanceRequired={minAdvanceRequired}
          isAdvanceSufficient={isAdvanceSufficient}
          showAdvancePolicy
          onClearForm={handleClearForm}
          onViewBucket={() => setBucketSheetOpen(true)}
          bucketItemCount={linesArray.length}
          viewBucketLabel="See all orders"
          viewBucketShortLabel="Bucket"
          showSettledInput={linesArray.length > 0}
          settledValue={settledPrice}
          onSettledChange={handleSettledChange}
          onSettledClear={handleSettledClear}
          showAdvanceInput={hasSettled}
          advanceValue={totalAdvance}
          onAdvanceChange={handleAdvanceChange}
          onAdvanceFull={handleAdvanceFull}
          onAdvanceClear={handleAdvanceClear}
        />
      </form>

      <WholesaleModelSidePanel
        item={panelModel}
        existingLine={panelModel ? selectedLines[panelModel._id] || null : null}
        open={Boolean(panelModel)}
        onClose={() => setPanelModel(null)}
        onAddToBucket={handleAddToBucket}
        onRemoveFromBucket={() => handleRemoveFromBucket(panelModel._id)}
      />

      <WholesaleOrderBucketSheet
        open={bucketSheetOpen}
        onClose={() => setBucketSheetOpen(false)}
        groupedLines={groupedLines}
        itemCount={linesArray.length}
        formatMoney={formatMoney}
        onEditLine={handleEditLine}
        onRemoveLine={handleRemoveFromBucket}
        grandTotal={grandTotal}
        totalAdvance={totalAdvance}
        balance={balance}
      />
    </div>
  );
};

export default WholesaleOrderPanelForm;
